import React, { FC, MutableRefObject, useRef } from "react";
import { ExternalChatChannel, ExternalChatMessage, ExternalChatOrganization, ExternalChatUser, } from "domain-entities";
import { Box, Button, makeStyles, OutlinedInput } from "@material-ui/core";
import { CollectionStore } from "Helpers/StoreHelper";
import { joiResolver } from "@hookform/resolvers";
import Joi from "joi";
import { useTranslation } from "Providers/TranslationProvider";
import { useSnackbar } from "material-ui-snackbar-provider";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import moment from "moment";
import { color, px } from "Configs/theme";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { useApp } from "Providers/AppProvider";
import { getColorRgbaFromHex } from "../../Helpers/ColorHelper";

interface Props {
  messagesStore: CollectionStore<ExternalChatMessage>;
  organization: ExternalChatOrganization;
  viewerUser: ExternalChatUser;
  channel: ExternalChatChannel;
  readChannel: () => void;
  sendFileRef: MutableRefObject<((file: File) => void) | undefined>;
}

interface FormValues {
  text: string;
}

const MessageForm: FC<Props> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { firebaseStorage } = useApp();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const defaultValues: FormValues = {
    text: "",
  };

  const schema = Joi.object({
    text: Joi.string()
      .required()
      .messages({
        "string.empty": t("Common_Form_Error_MandatoryField", {
          field: t("MessageForm_Label_Text"),
        }),
      }),
  });

  const form = useForm<FormValues>({
    resolver: joiResolver(schema),
    defaultValues,
  });

  function onSubmit(formValues: FormValues) {
    sendMessage(`MS-${v4()}`, { text: formValues.text });
  }

  async function sendMessage(
    messageId: string,
    message?: Partial<ExternalChatMessage>
  ) {
    try {
      const nextMessage: ExternalChatMessage = {
        id: messageId,
        sender: props.viewerUser.id,
        source: props.viewerUser.type,
        state: "sent",
        timestamp: moment().valueOf(),
        ...message,
      };

      form.reset();
      await props.messagesStore.setItem(nextMessage);
    } catch (error) {
      snackbar.showMessage(error.message);
    }
  }

  function onSelectFile(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.files) {
      Array.from(event.target.files).forEach(sendFile);
    }
  }

  async function sendFile(file: File) {
    const messageId = `MS-${v4()}`;
    const extension = file.name.substr(
      file.name.lastIndexOf(".") + 1,
      file.name.length
    );

    const path = `${props.organization.id}/channels/${props.channel.id}/messages/${messageId}.${extension}`;

    try {
      if (firebaseStorage) {
        await firebaseStorage.ref(path).put(file);
        sendMessage(messageId, {
          media: {
            location: path,
            mimeType: file.type,
            name: file.name,
            thumbnails: [],
          },
        });
      }
    } catch (error) {
      snackbar.showMessage(error.message);
    }
  }

  props.sendFileRef.current = sendFile;

  return (
    <form
      className={classes.root}
      onSubmit={form.handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <OutlinedInput
          name="text"
          margin="dense"
          classes={{
            root: classes.messageTextField,
            notchedOutline: classes.messageTextFieldNotchedOutline
          }}
          onKeyPress={(event) => {
            props.readChannel();
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              form.handleSubmit(onSubmit)();
            }
          }}
          placeholder={t("MessageForm_Label_Text")}
          inputRef={form.register}
          rowsMax={4}
          multiline
          fullWidth
      />
      <Box className={classes.actions}>
        <input
          ref={uploadInputRef}
          type="file"
          onChange={onSelectFile}
          className={classes.uploadInput}
          multiple
        />
        <Button
          onClick={() => uploadInputRef.current?.click()}
          className={classes.button}
          size="small"
          variant="contained"
          color="secondary"
          disableElevation
        >
          <AttachFileIcon />
        </Button>
        <Button
          type="submit"
          className={classes.button}
          size="small"
          variant="contained"
          color="primary"
          disableElevation
        >
          <SendIcon />
        </Button>
      </Box>
    </form>
  );
};

export default MessageForm;

const useStyles = makeStyles({
  root: {
    display: "flex",
    marginTop: px.m,
    marginRight: px.l,
  },
  actions: {
    display: "flex",
    alignItems: "flex-end",
  },
  button: {
    marginLeft: px.m,
    height: 40,
  },
  uploadInput: {
    display: "none",
  },
  messageTextField: {
    backgroundColor: getColorRgbaFromHex(color.grayBackground00, 0.50),
  },
  messageTextFieldNotchedOutline: {
    borderWidth: "0 !important",
  },
});
